import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ShieldCheck, Phone, Lock } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Session-40: 2FA method chooser + access-code path
  const [needs2FA, setNeeds2FA] = useState(false);
  const [accessCodeMode, setAccessCodeMode] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [acBusy, setAcBusy] = useState(false);
  const [appName, setAppName] = useState('DWS Vault Chat');

  const { login, user } = useAuth();

  // Fetch app name
  useEffect(() => {
    const fetchAppName = async () => {
      try {
        const response = await axios.get(`${API}/settings/support-info`);
        if (response.data.app_name) {
          setAppName(response.data.app_name);
          document.title = `${response.data.app_name} - Admin Login`;
        }
      } catch (error) {
        console.error('Failed to fetch app name:', error);
      }
    };
    fetchAppName();
  }, []);

  const navigate = useNavigate();

  // Auto-redirect only if already logged in AS ADMIN (matching role)
  // If user is logged in as different role, allow them to see this page and log in as admin
  useEffect(() => {
    if (user && user.role === 'super_admin') {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Get or generate persistent device_id (one per browser)
      let deviceId = localStorage.getItem('admin_device_id');
      if (!deviceId) {
        deviceId = 'dev_' + (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2) + Date.now());
        localStorage.setItem('admin_device_id', deviceId);
      }

      const response = await axios.post(`${API}/auth/login`, {
        phone,
        password,
        device_id: deviceId,
      });

      const data = response.data;

      // ADMIN 2FA: Server signals that Google verification is required
      if (data.requires_google_verification) {
        // Persist verification context — Session-40: allow user to choose
        // between Google Auth and Access Code (6-digit OTP).
        sessionStorage.setItem('admin_2fa_temp_token', data.temp_token);
        sessionStorage.setItem('admin_2fa_admin_id', data.admin_id);
        sessionStorage.setItem('admin_2fa_device_id', deviceId);
        // Show method chooser instead of immediate Google redirect
        setNeeds2FA(true);
        setLoading(false);
        return;
      }

      // Trusted device path — regular login flow
      const userData = data;

      // STRICT ROLE CHECK: Only allow admin on this page
      if (userData.role !== 'super_admin') {
        setError('This is Admin login page. Please use the correct login page for your role.');
        setLoading(false);
        return;
      }

      login(userData);

      // Track activity for login (fire-and-forget — must not block navigation)
      axios.post(`${API}/admin/activity/track`, {
        user_id: userData.id,
        action: 'login'
      }).catch((trackErr) => console.error('Activity tracking failed:', trackErr));

      navigate('/admin-dashboard');
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many attempts from this device — please wait a minute and try again.');
      } else {
        setError(err.response?.data?.detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center p-4" style={{ position: 'fixed', inset: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md my-8">
        <div className="text-center mb-8">
          <div className="w-28 h-28 mx-auto mb-4 flex items-center justify-center">
            <img 
              src="/icons/customer/icon-128x128.png" 
              alt="DWS Vault Chat"
              className="w-28 h-28 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-800">{appName}</h1>
          <p className="text-gray-600 mt-2">Admin Login</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          {/* Session-40: 2FA method chooser overlay */}
          {needs2FA && !accessCodeMode && (
            <div className="space-y-3">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                🔐 Second factor required. Choose a verification method:
              </div>
              <button type="button"
                      onClick={() => {
                        const redirectUrl = window.location.origin + '/admin-auth-callback';
                        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                      }}
                      className="w-full py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold"
                      data-testid="admin-2fa-google-btn">
                Continue with Google
              </button>
              <button type="button"
                      onClick={async () => {
                        setAcBusy(true);
                        try {
                          await axios.post(`${API}/admin/2fa/access-code/request`, {
                            admin_id: sessionStorage.getItem('admin_2fa_admin_id'),
                            temp_token: sessionStorage.getItem('admin_2fa_temp_token'),
                            device_id: sessionStorage.getItem('admin_2fa_device_id'),
                          });
                          setAccessCodeMode(true);
                        } catch (e) {
                          setError(e?.response?.data?.detail || 'Failed to request access code');
                        } finally { setAcBusy(false); }
                      }}
                      disabled={acBusy}
                      className="w-full py-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold disabled:opacity-50"
                      data-testid="admin-2fa-accesscode-btn">
                {acBusy ? 'Requesting…' : 'Use Access Code'}
              </button>
              <button type="button"
                      onClick={() => { setNeeds2FA(false); }}
                      className="w-full text-xs text-gray-500 hover:text-gray-700 py-1">
                ← Back
              </button>
            </div>
          )}

          {/* Access Code entry screen */}
          {needs2FA && accessCodeMode && (
            <div className="space-y-3">
              <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-800">
                🔑 Enter the 5-digit access code from your other admin session (Settings → Two-Way Security).
                Code expires in 5 minutes.
              </div>
              <input type="text" value={accessCode} maxLength={5} inputMode="numeric"
                     onChange={(e) => setAccessCode(e.target.value.replace(/\D/g, ''))}
                     placeholder="00000"
                     className="w-full text-center tracking-[0.5em] text-xl font-mono py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                     data-testid="admin-2fa-accesscode-input" />
              <button type="button" disabled={acBusy || accessCode.length !== 5}
                      onClick={async () => {
                        setAcBusy(true); setError('');
                        try {
                          const res = await axios.post(`${API}/admin/2fa/access-code/verify`, {
                            admin_id: sessionStorage.getItem('admin_2fa_admin_id'),
                            temp_token: sessionStorage.getItem('admin_2fa_temp_token'),
                            device_id: sessionStorage.getItem('admin_2fa_device_id'),
                            code: accessCode,
                          });
                          sessionStorage.removeItem('admin_2fa_temp_token');
                          sessionStorage.removeItem('admin_2fa_admin_id');
                          sessionStorage.removeItem('admin_2fa_device_id');
                          login(res.data);
                        } catch (e) {
                          setError(e?.response?.data?.detail || 'Verify failed');
                        } finally { setAcBusy(false); }
                      }}
                      className="w-full py-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold disabled:opacity-50"
                      data-testid="admin-2fa-accesscode-verify-btn">
                {acBusy ? 'Verifying…' : 'Verify & Sign In'}
              </button>
              <button type="button" onClick={() => { setAccessCodeMode(false); setAccessCode(''); }}
                      className="w-full text-xs text-gray-500 hover:text-gray-700 py-1">
                ← Choose another method
              </button>
            </div>
          )}

          {!needs2FA && (<>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <div className="relative">
              <Phone size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter phone number"
                data-testid="admin-login-phone-input"
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                data-testid="admin-login-password-input"
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Google 2FA notice */}
          <div className="bg-purple-50 border border-purple-200 text-purple-800 px-3 py-2 rounded-lg text-xs flex items-start gap-2">
            <ShieldCheck size={16} weight="fill" className="text-purple-600 mt-0.5 flex-shrink-0" />
            <span>
              Admin login is protected by Google 2FA. On a new device, we'll ask you to verify with your admin Gmail after entering your password.
            </span>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>

          {/* Forgot Password Link */}
          <button
            type="button"
            onClick={() => navigate('/admin-forgot-password')}
            className="w-full text-sm text-purple-600 hover:text-purple-700 font-medium mt-2"
            data-testid="admin-forgot-password-link"
          >
            Forgot Password?
          </button>
          </>)}
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;